<template>
    <div v-loading="loading">
        <hts-form-date
            v-model="filters.dateRange"
            range
            type="dateRange"
            prop="dateRange"
            :placeholder="$t('labels.fromTo')"
            class="max-w-96 max-h-96" />
        <div class="my-4">
            <h2>
                Xe
            </h2>
            <hts-data-table
                class="w-full mt-4"
                :headers="headers"
                :items="items || []" />
        </div>
    </div>
</template>

<script setup lang="ts">
const { $formatters } = useNuxtApp()
const { reportPage } = $store()
const loading = ref(true)
const headers = [
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicle'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'typeName',
    headerName: $t('labels.vehicleType'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'tripCount',
    headerName: $t('labels.tripNumber'),
    valueGetter: (params: any) => {
      const { tripCount } = params.data
      return $formatters.formatNumber(tripCount || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'journeyCount',
    headerName: $t('labels.journeyNumber'),
    valueGetter: (params: any) => {
      const { journeyCount } = params.data
      return $formatters.formatNumber(journeyCount || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'totalDistance',
    headerName: $t('labels.kmCount'),
    valueGetter: (params: any) => {
      const { totalDistance } = params.data
      return $formatters.formatNumber(totalDistance || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'totalRevenue',
    headerName: $t('labels.revenue'),
    valueGetter: (params: any) => {
      const { totalRevenue } = params.data
      return $formatters.formatNumber(totalRevenue || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'totalCost',
    headerName: $t('labels.cost'),
    valueGetter: (params: any) => {
      const { totalCost } = params.data
      return $formatters.formatNumber(totalCost || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'totalProfit',
    headerName: $t('labels.profit'),
    valueGetter: (params: any) => {
      const { totalProfit } = params.data
      return $formatters.formatNumber(totalProfit || 0)
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'directRouteRate',
    headerName: $t('labels.directRouteRate'),
    valueGetter: (params: any) => {
      const journeyCount = $formatters.formatNumber(params.data.journeyCount || 0)
      const totalStartAtParking = $formatters.formatNumber(params.data.totalStartAtParking || 0)

      if (!journeyCount) {
        return ''
      }

      return `${journeyCount - totalStartAtParking}/${journeyCount}`
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    field: 'parkingRouteRate',
    headerName: $t('labels.parkingRouteRate'),
    valueGetter: (params: any) => {
      const journeyCount = $formatters.formatNumber(params.data.journeyCount || 0)
      const totalStartAtParking = $formatters.formatNumber(params.data.totalStartAtParking || 0)

      if (!journeyCount) {
        return ''
      }

      return `${totalStartAtParking}/${journeyCount}`
    },
    sortable: true,
    filter: 'agNumberColumnFilter'
  }
]

const items = ref<any[]>([])

const filters = computed({
  get () {
    return reportPage.filters
  },
  set (val: any) {
    reportPage.setFilters(val)
  }
})

onMounted(() => {
  getReport()
})

/** Watching */
watch(
  () => filters.value,
  (val: any) => {
    loadReport()
  },
  { deep: true }
)

/** API requests */
const getReport = () => {
  items.value = []
  $api($url.report.getVehicle, { body: filters.value })
    .then((data: any) => {
      const responseData: any = data.data.value
      if (responseData?.success) {
        items.value = responseData.data
      } else {
        items.value = []
      }
      loading.value = false
    })
    .catch(() => {
      loading.value = false
    })
}

const loadReport = $lodash.debounce(getReport, 500)
</script>
